import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook for keyboard shortcuts
 * Handles global keyboard shortcuts like / for search, Esc for modals, etc.
 */
const useKeyboardShortcuts = ({
  onSearchFocus,
  onModalClose,
  onHelpToggle,
  enabled = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      // Don't trigger shortcuts if user is typing in an input/textarea
      if (
        event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.isContentEditable
      ) {
        // Allow Esc to work even in inputs
        if (event.key === 'Escape') {
          event.preventDefault();
          if (onModalClose) {
            onModalClose();
          }
          // Also blur the input
          if (event.target.tagName === 'INPUT') {
            event.target.blur();
          }
        }
        return;
      }

      // Don't trigger if shortcuts are disabled
      if (!enabled) return;

      // `/` - Focus search
      if (event.key === '/' && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        event.preventDefault();
        if (onSearchFocus) {
          onSearchFocus();
        } else {
          // Try to find search input in the header
          const searchInput = document.querySelector('header input[type="text"]');
          if (searchInput) {
            searchInput.focus();
            searchInput.select();
          }
        }
      }

      // `Esc` - Close modals/dropdowns
      if (event.key === 'Escape') {
        if (onModalClose) {
          onModalClose();
        }
        // Close any open dropdowns
        const dropdowns = document.querySelectorAll('[data-dropdown-open="true"]');
        dropdowns.forEach((dropdown) => {
          dropdown.setAttribute('data-dropdown-open', 'false');
        });
      }

      // `?` - Show help modal
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        event.preventDefault();
        if (onHelpToggle) {
          onHelpToggle();
        }
      }

      // Arrow keys for navigation (when not in input)
      if (event.key === 'ArrowLeft' && event.altKey) {
        event.preventDefault();
        navigate(-1); // Go back
      }

      if (event.key === 'ArrowRight' && event.altKey) {
        event.preventDefault();
        navigate(1); // Go forward
      }
    },
    [enabled, navigate, onSearchFocus, onModalClose, onHelpToggle]
  );

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [enabled, handleKeyDown]);

  return {
    searchInputRef,
  };
};

export default useKeyboardShortcuts;

